import React, { useState, useEffect, useCallback } from 'react';
import Reveal from './Reveal';
import './Audiovisual.css';

/**
 * Seção Audiovisual — cinco curtas-metragens (os dois primeiros são os mais recentes).
 * Para exibir o vídeo, preencha `youtubeId` OU `vimeoId` de cada obra
 * (assim o site continua leve, sem hospedar o arquivo). Enquanto vazio,
 * a capa mostra "Vídeo em breve" e o roteiro em PDF fica disponível.
 * As capas (`thumbnail`) foram extraídas dos pôsteres dos próprios roteiros.
 */
const films = [
  {
    id: 'ilha-dos-tubaroes',
    title: 'Ilha dos Tubarões: O Segredo do Código 4000',
    tagline: 'Quando Tone promete, Tone cumpre.',
    genre: 'Ação / Drama / Aventura',
    duration: 'Curta-metragem',
    location: '',
    isNew: true,
    synopsis:
      'Numa ilha de pedra isolada no meio do oceano fica o presídio de segurança máxima da Ilha dos Tubarões — um lugar esquecido até por Deus, onde homens e mulheres trabalham até a exaustão e as mães são separadas dos filhos. Ali, o detento Tone jura voltar. Depois de ouvir dos guardas o segredo do Código 4000 e do mapa com os túneis da ilha, ele foge com os amigos Pedreira e Delamite, reúne uma equipe e retorna para libertar todos os presos e derrubar, de vez, o poder dos que comandam aquele lugar.',
    roteiroPdf: '/ilhadostubaroes.pdf',
    thumbnail: '/poster-ilha.jpg',
    accent: 'clay',
    youtubeId: '', // TODO: inserir o ID do YouTube/Vimeo quando o vídeo estiver no ar
    vimeoId: ''
  },
  {
    id: 'medalha-de-um-dragao',
    title: 'A Medalha de um Dragão',
    tagline: 'Esta medalha não é de ninguém além da minha família.',
    genre: 'Ação / Artes marciais',
    duration: 'Curta-metragem',
    location: '',
    isNew: true,
    synopsis:
      'Iheitokk, conhecido como o Dragão, guarda com carinho a medalha de ouro que os pais lhe deixaram — a honra e a glória da família, para passar de geração em geração. Quando três marginais a serviço do poderoso chefão Piook a roubam, ele tem uma visão dos pais à beira de um lago e jura lutar com todas as forças para recuperá-la. Uma história de coragem, memória e justiça, do assalto na parada de ônibus ao confronto final na mansão de Piook.',
    roteiroPdf: '/medalhadeumdragao.pdf',
    thumbnail: '/poster-dragao.jpg',
    accent: 'wine',
    youtubeId: '',
    vimeoId: ''
  },
  {
    id: 'monica-batista',
    title: 'A Mônica e Batista Viveram um Verdadeiro Amor',
    tagline: 'Amar é escolher. E escolher é ser livre.',
    genre: 'Romance / Drama',
    duration: '15–20 min',
    location: 'Rio de Janeiro',
    synopsis:
      'Batista, um jovem sensível e sonhador, conhece Mônica num encontro casual na Ilha do Fundão. O romance nasce instantaneamente, mas é interrompido pelo pai dela — um capitão da Marinha dominado por um ciúme doentio. Mesmo vigiada, Mônica decide fugir para viver seu amor. A fuga ganha proporções públicas, envolvendo autoridades e imprensa, até que ela assume sua própria voz diante de todos. Uma história sobre liberdade, afeto e a coragem de enfrentar o medo para viver o amor verdadeiro.',
    roteiroPdf: '/monicaebatista.pdf',
    thumbnail: '/poster-monica.jpg',
    accent: 'wine',
    youtubeId: '', // TODO: inserir o ID do YouTube/Vimeo quando o vídeo estiver no ar
    vimeoId: ''
  },
  {
    id: 'india-cacadores',
    title: 'A Índia e os Caçadores',
    tagline: 'Entre a floresta e a perseguição, ela escolhe a liberdade.',
    genre: 'Aventura / Drama',
    duration: 'Curta-metragem',
    location: '',
    synopsis:
      'Na imensidão da selva, uma índia é capturada por dois caçadores. Entre fugas a cavalo, lobos famintos e a perseguição de tribos guerreiras, nasce uma convivência improvável — até que ela precisa escolher entre os companheiros e o chamado da própria aldeia. Uma fábula sobre liberdade e pertencimento, narrada ao som da pátria amada.',
    roteiroPdf: '/umencontronaselva-assinado.pdf',
    thumbnail: '/poster-india.jpg',
    accent: 'green',
    youtubeId: '',
    vimeoId: ''
  },
  {
    id: 'cena-arena-maldita',
    title: 'Cena Arena Maldita 1',
    tagline: 'Aqui eu mando.',
    genre: 'Ação / Drama',
    duration: 'Curta-metragem',
    location: '',
    synopsis:
      'Numa arena onde a ordem é lei, cinco homens entram para cinco dias de pura guerra — mas só um sairá coroado rei. Entre a juíza implacável Malena, o apresentador Maré que eletriza a multidão e lutadores movidos por glória, ambição e esperança, desenrola-se um espetáculo brutal de força e justiça. "Aqui quem manda sou eu."',
    roteiroPdf: '/cenamaldita.pdf',
    thumbnail: '/poster-cena.jpg',
    accent: 'clay',
    youtubeId: '',
    vimeoId: ''
  }
];

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor" aria-hidden="true">
    <path d="M8 5.14v13.72a1 1 0 0 0 1.54.84l10.3-6.86a1 1 0 0 0 0-1.68L9.54 4.3A1 1 0 0 0 8 5.14Z" />
  </svg>
);

const embedSrc = (film) => {
  if (film.youtubeId) return `https://www.youtube.com/embed/${film.youtubeId}?autoplay=1`;
  if (film.vimeoId) return `https://player.vimeo.com/video/${film.vimeoId}?autoplay=1`;
  return null;
};

const Audiovisual = () => {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === 'Escape' && close();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [active, close]);

  return (
    <section id="audiovisual" className="audiovisual section">
      <div className="container">
        <Reveal>
          <span className="section-eyebrow">Cinema &amp; roteiro</span>
          <h2 className="section-title">Audiovisual</h2>
          <p className="section-subtitle">
            Cinco curtas-metragens escritos por Varella — histórias de amor, selva, arena,
            prisão e honra. Assista ao trailer ou leia o roteiro completo.
          </p>
        </Reveal>

        <div className="av-grid">
          {films.map((film, index) => {
            const hasVideo = Boolean(film.youtubeId || film.vimeoId);
            return (
              <Reveal as="article" key={film.id} className="av-card" delay={(index % 3) * 90}>
                <div
                  className={`av-poster ${film.thumbnail ? 'has-thumb' : `accent-${film.accent}`} ${hasVideo ? 'is-playable' : ''}`}
                  onClick={() => hasVideo && setActive(film)}
                  role={hasVideo ? 'button' : undefined}
                  tabIndex={hasVideo ? 0 : undefined}
                  onKeyDown={(e) => hasVideo && (e.key === 'Enter' || e.key === ' ') && setActive(film)}
                  aria-label={hasVideo ? `Assistir ${film.title}` : undefined}
                >
                  {film.thumbnail ? (
                    <>
                      <img src={film.thumbnail} alt={`Pôster do curta ${film.title}`} className="av-thumb" loading="lazy" />
                      <span className="av-scrim" aria-hidden="true"></span>
                    </>
                  ) : (
                    <span className="av-poster-title">{film.title}</span>
                  )}

                  {film.isNew && <span className="av-new">Novo</span>}
                  <span className={`av-play ${hasVideo ? '' : 'av-play--muted'}`}><PlayIcon /></span>
                  {!hasVideo && <span className="av-soon">Vídeo em breve</span>}
                </div>

                <div className="av-body">
                  <h3 className="av-title">{film.title}</h3>
                  {film.tagline && <p className="av-tagline">“{film.tagline}”</p>}
                  {(film.genre || film.duration || film.location) && (
                    <div className="av-meta">
                      {[film.genre, film.duration, film.location].filter(Boolean).join(' · ')}
                    </div>
                  )}
                  <p className="av-synopsis">{film.synopsis}</p>
                  <a href={film.roteiroPdf} target="_blank" rel="noopener noreferrer" className="av-roteiro">
                    Ler o roteiro (PDF) →
                  </a>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>

      {active && (
        <div className="av-modal" role="dialog" aria-modal="true" aria-label={active.title} onClick={close}>
          <button className="av-modal-close" onClick={close} aria-label="Fechar">×</button>
          <div className="av-modal-inner" onClick={(e) => e.stopPropagation()}>
            <div className="av-video-wrap">
              <iframe
                src={embedSrc(active)}
                title={active.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <h3 className="av-modal-title">{active.title}</h3>
          </div>
        </div>
      )}
    </section>
  );
};

export default Audiovisual;
